import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { PROJECT_CATEGORIES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", category: PROJECT_CATEGORIES[0], location: "", desc: "", img: "" };
const inputCls =
  "w-full border border-white/15 bg-[#050505] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#0055FF]";

export default function ProjectsManager({ token, onUnauthorized }) {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects`);
      setProjects(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleErr = (e, fallback) => {
    if (e.response?.status === 401) onUnauthorized?.();
    toast.error(e.response?.data?.detail || fallback);
  };

  const save = async () => {
    try {
      if (form.id) await axios.put(`${API}/admin/projects/${form.id}`, form, { headers });
      else await axios.post(`${API}/admin/projects`, form, { headers });
      toast.success(form.id ? "Project updated" : "Project added");
      setForm(null);
      load();
    } catch (e) {
      handleErr(e, "Failed to save project");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/projects/${p.id}`, { headers });
      toast.success("Project deleted");
      load();
    } catch (e) {
      handleErr(e, "Failed to delete project");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload-image`, fd, { headers });
      setForm((f) => ({ ...f, img: `/api/uploads/${r.data.path}` }));
      toast.success("Image uploaded");
    } catch (e) {
      handleErr(e, "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="projects-manager" className="mt-8">
      <div className="flex items-center justify-between">
        <p className="max-w-2xl text-sm leading-relaxed text-[#A3A3A3]">
          Add, edit or remove projects shown in the gallery. Upload real project photos to replace demo images.
        </p>
        <button
          data-testid="project-add-button"
          onClick={() => setForm({ ...EMPTY })}
          className="flex items-center gap-2 bg-[#0055FF] px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF]"
        >
          <Plus size={14} /> Add Project
        </button>
      </div>

      {form && (
        <div data-testid="project-form" className="mt-6 border border-[#0055FF]/40 bg-[#0A0A0A] p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">{form.id ? "Edit Project" : "New Project"}</h3>
            <button data-testid="project-form-cancel-button" onClick={() => setForm(null)} aria-label="Close form">
              <X size={18} className="text-white/60 hover:text-white" />
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="project-form-title-input"
              placeholder="Project title *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />
            <select
              data-testid="project-form-category-select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className={`${inputCls} appearance-none`}
            >
              {PROJECT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              data-testid="project-form-location-input"
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={inputCls}
            />
            <div className="flex gap-2">
              <input
                data-testid="project-form-img-input"
                placeholder="Image URL (https:// or uploaded)"
                value={form.img}
                onChange={(e) => setForm({ ...form, img: e.target.value })}
                className={inputCls}
              />
              <label
                data-testid="project-form-upload"
                className={`flex shrink-0 cursor-pointer items-center gap-1.5 bg-white px-4 text-[11px] font-bold uppercase tracking-widest text-[#050505] hover:bg-white/80 ${
                  uploading ? "pointer-events-none opacity-50" : ""
                }`}
              >
                <Upload size={12} /> {uploading ? "..." : "Upload"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    upload(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <textarea
              data-testid="project-form-desc-input"
              placeholder="Short description"
              rows={3}
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
              className={`${inputCls} resize-none sm:col-span-2`}
            />
          </div>
          {form.img && <img src={form.img} alt="preview" className="mt-4 aspect-[16/9] w-48 border border-white/10 object-cover" />}
          <button
            data-testid="project-form-save-button"
            onClick={save}
            disabled={!form.title.trim()}
            className="mt-5 bg-[#0055FF] px-8 py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF] disabled:opacity-40"
          >
            {form.id ? "Save Changes" : "Add Project"}
          </button>
        </div>
      )}

      <div className="mt-6 space-y-3" data-testid="projects-list">
        {projects.map((p) => (
          <div key={p.id} data-testid={`project-row-${p.id}`} className="flex items-center gap-4 border border-white/10 bg-[#0A0A0A] p-3">
            <img src={p.img} alt={p.title} className="h-14 w-20 shrink-0 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display font-semibold">{p.title}</p>
              <p className="text-xs text-[#A3A3A3]">
                {p.category} · {p.location}
              </p>
            </div>
            <button
              data-testid={`project-edit-${p.id}`}
              onClick={() => setForm({ ...p })}
              className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-[#0055FF]"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              data-testid={`project-delete-${p.id}`}
              onClick={() => remove(p)}
              className="flex items-center gap-1.5 border border-red-500/40 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
